import { observer } from 'mobx-react-lite';
import posthog from 'posthog-js';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { IEvent, ReportModel } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  Fieldset,
  ImagePicker,
  Input,
  SaveButtonValid,
} from '@feathr/components';
import TagsField from '@feathr/extender/components/TagsField';
import { useActionBar, useLocalUrl } from '@feathr/extender/state';
import { flattenErrors, logUserEvents } from '@feathr/hooks';

import { BillingFields, ProjectInfoFields } from './ProjectFormFields';
import { useProjectForm } from './useProjectForm';

import * as styles from './EventAdd.css';

function EventAdd(): JSX.Element {
  const { t } = useTranslation();
  const { setRightActions } = useActionBar();
  const history = useHistory();
  const localUrl = useLocalUrl();

  const { event, billable, validationErrors, handleBillingChange } = useProjectForm();

  function handleCancel(): void {
    history.push(localUrl('/projects'));
  }

  function handleChangeTags(newValue: string[]): void {
    event.set({ tag_ids: newValue });
  }

  function handleSaveEvent(savedEvent: ReportModel<IEvent>): void {
    logUserEvents({ 'Created project': { eventId: savedEvent.id } });
    posthog.capture('project_created', {
      project_id: savedEvent.id,
      account_id: sessionStorage.getItem('accountId'),
    });
    history.push(localUrl(event.getItemUrl()));
  }

  useEffect(() => {
    setRightActions([
      <Button key={'cancel'} onClick={handleCancel}>
        {t('Cancel')}
      </Button>,
      <SaveButtonValid
        disabled={!event.isDirty}
        errorMessage={t("Couldn't add project. Try again.")}
        errors={flattenErrors(validationErrors)}
        key={'save_project'}
        method={'add'}
        model={event}
        onSave={handleSaveEvent}
        successMessage={t('Successfully added new Project.')}
      >
        {t('Save')}
      </SaveButtonValid>,
    ]);
  }, [event.isValid()]);

  const BILLING_LEARN_MORE_LINK =
    'https://help.feathr.co/hc/en-us/articles/360037323074-Feathr-Billing-Basics';
  const billingDescription = (
    <>
      <p>
        {t(
          'Select a billing configuration for this project. You have two options to pay for media for ad campaigns in this project: pay as you go or pre-pay.',
        )}
      </p>
      <a href={BILLING_LEARN_MORE_LINK} rel={'noreferrer'} target={'_blank'}>
        {t('Learn more about billing')}
      </a>
    </>
  );

  return (
    <>
      <Card>
        <Card.Header
          description={t('Name and tag your project so you can find it later, then add a logo.')}
          title={t('Project details')}
        />
        <Card.Content>
          <ProjectInfoFields event={event} showLogo={false} />
          <Fieldset>
            <TagsField
              context={'event'}
              helpText={t(
                'Search for an existing tag, or type a new name to create one. Tags group related projects so you can filter for them in the projects list.',
              )}
              label={t('Tags')}
              onChange={handleChangeTags}
              value={event.get('tag_ids', [])}
            />
          </Fieldset>
        </Card.Content>
        <Card.Content>
          <ImagePicker
            attribute={'logo'}
            buttonText={t('Upload project photo')}
            helpText={t(
              'Upload an image to represent this project. This makes it easier to find this project in your projects list and customizes the look of its reports.',
            )}
            imgClassName={styles.image}
            label={t('Project logo')}
            model={event}
            optional={true}
            value={event.get('logo')}
          />
        </Card.Content>
      </Card>
      <Card>
        <Card.Header
          description={t(
            'Feathr automatically creates an email subscription for each project. Customize the label recipients see when managing their subscription preferences.',
          )}
          title={t('Email subscriptions')}
        />
        <Card.Content>
          <Fieldset>
            <Input
              attribute={['email_preferences_alias']}
              helpText={t('Defaults to the project name.')}
              label={t('Custom email subscription label')}
              model={event}
              placeholder={event.get('name')}
              type={'text'}
            />
          </Fieldset>
        </Card.Content>
      </Card>
      <Card>
        <Card.Header description={billingDescription} title={t('Billing')} />
        <Card.Content>
          <BillingFields billable={billable} event={event} onBillingChange={handleBillingChange} />
        </Card.Content>
      </Card>
    </>
  );
}

export default observer(EventAdd);
